import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { connect, DataItem } from 'scrivito'

import { canEditRecord, canManageTeamRecords } from './recordsScope'
import { TimerecordRow } from './TimerecordRow'
import { useAllTimesData } from './useAllTimesData'
import { useExportAllTimes } from './useExportAllTimes'
import { TimeTrackingControlBar } from '../../components/TimeTrackingControlBar'
import { useTeamLeaders } from '../../hooks/useTeamLeaders'
import { useTeamMembers } from '../../hooks/useTeamMembers'
import { TimeTrackingTab, FilterValues, AllTimesTabProps } from '../../types'
import { getCurrentWeekRange, navigateWeek } from '../../utils/dateUtils'
import {
  deleteTimeRecord,
  duplicateTimeRecord,
  getTimeRecordViewData,
  TimeRecordViewData,
  updateMultipleTimeRecordStatuses,
} from '../../utils/timerecordMethods'
import { ConfirmActionModal } from '../components/ConfirmActionModal'
import { ViewLogTimeDrawer } from '../myTimes/ViewLogTimeDrawer'
import { Column } from '@/Atomic/Templates/Table'
import { DisplayCountIndicator } from '@/Components/DisplayCountIndicator'
import { Table } from '@/Components/Table'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/typesV2'
import { getSchemaName } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'
import { calculateTimerecordTotals } from '@/utils/timerecordUtils'

const AllTimesTab = connect(
  ({
    openLogTimeDrawer,
    initialFilters,
    weekRange,
    onWeekRangeChange,
    onFilterChange,
    currentUserId,
  }: AllTimesTabProps) => {
    const { t } = useTranslation()

    const filters: FilterValues = initialFilters || {
      status: null,
      team: null,
      customer: null,
      order: null,
      workPackage: null,
    }

    const initialLeaderId = initialFilters?.teamLeaderId
      ? initialFilters.teamLeaderId
      : initialFilters?.isTeamLeader && initialFilters?.team
        ? initialFilters.team
        : ''
    const [selectedLeaderId, setSelectedLeaderId] =
      useState<string>(initialLeaderId)

    const effectiveWeekRange =
      initialFilters?.dateRange ||
      (initialFilters?.filterByDate
        ? {
            startDate: initialFilters.filterByDate,
            endDate: initialFilters.filterByDate,
          }
        : weekRange)

    const {
      timeRecordItems: dataItems,
      hasMore,
      loadMore,
    } = useAllTimesData(filters, effectiveWeekRange)

    const totals = calculateTimerecordTotals(dataItems)

    const canManage = canManageTeamRecords(currentUserId)
    const isEditable = (item: DataItem) =>
      canEditRecord(item, currentUserId, canManage)
    const editableItems = dataItems.filter(isEditable)

    const { isExporting, exportCSV } = useExportAllTimes({
      filters,
      weekRange: effectiveWeekRange,
    })

    const teamLeaders = useTeamLeaders()
    const teamLeaderIds = teamLeaders.map((l) => l.value)
    const { members: teamMemberOptions } = useTeamMembers(selectedLeaderId)

    const [viewDrawerOpen, setViewDrawerOpen] = useState(false)
    const [selectedRecordId, setSelectedRecordId] = useState<string | null>(
      null,
    )
    const [selectedRecordData, setSelectedRecordData] =
      useState<TimeRecordViewData | null>(null)
    const [selectedRecordIds, setSelectedRecordIds] = useState<Set<string>>(
      new Set(),
    )
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

    const isSelectedRecordEditable = editableItems.some(
      (item) => item.id() === selectedRecordId,
    )

    const handleFilterChange = onFilterChange

    const handleNavigateWeek = (direction: 'prev' | 'next') => {
      const newRange = navigateWeek(effectiveWeekRange, direction)
      onWeekRangeChange(newRange)
    }

    const goToCurrentWeek = () => {
      onWeekRangeChange(getCurrentWeekRange())
    }

    const handleLogTime = () => {
      openLogTimeDrawer()
    }

    const handleDeleteTimeRecord = (recordId: string) => {
      setPendingDeleteId(recordId)
    }

    const confirmDelete = () => {
      if (pendingDeleteId) deleteTimeRecord(pendingDeleteId)
      setPendingDeleteId(null)
    }

    const handleDuplicateTimeRecord = (recordId: string) => {
      duplicateTimeRecord(recordId)
    }

    const handleAddTimeRecord = (item: DataItem) => {
      openLogTimeDrawer(undefined, {
        customer: ensureString(item.get('client')),
        order: ensureString(item.get('order')),
        workPackage: ensureString(item.get('position')),
        date: item.get('recordedAt')
          ? new Date(ensureString(item.get('recordedAt'))).toISOString()
          : new Date().toISOString(),
        productType: ensureString(item.get('productType')),
      })
    }

    const handleViewTimeRecord = async (recordId: string) => {
      const recordData = await getTimeRecordViewData(recordId)
      if (recordData) {
        setSelectedRecordId(recordId)
        setSelectedRecordData(recordData)
        setViewDrawerOpen(true)
      }
    }

    const handleToggleRecordSelection = (recordId: string) => {
      setSelectedRecordIds((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(recordId)) {
          newSet.delete(recordId)
        } else {
          newSet.add(recordId)
        }
        return newSet
      })
    }

    const handleSelectAll = () => {
      if (selectedRecordIds.size === editableItems.length) {
        setSelectedRecordIds(new Set())
      } else {
        setSelectedRecordIds(new Set(editableItems.map((item) => item.id())))
      }
    }

    const isAllSelected =
      editableItems.length > 0 &&
      selectedRecordIds.size === editableItems.length

    const handleBatchStatusUpdate = async (statusValue: string) => {
      if (selectedRecordIds.size === 0) return

      const selectedItems = editableItems.filter((item) =>
        selectedRecordIds.has(item.id()),
      )

      try {
        await updateMultipleTimeRecordStatuses(selectedItems, statusValue)
        setSelectedRecordIds(new Set())
      } catch (error) {
        console.error('Failed to update statuses:', error)
      }
    }

    const { schema } = getSchema(getSchemaName(DataClassName.TIMERECORD))
    const schemaAttributes = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )

    const statusAttribute = schemaAttributes?.status
    const statusOptions = statusAttribute?.enumValues || []

    const statuses = statusOptions.map((enumValue) => ({
      label: enumValue.title,
      value: enumValue.value,
    }))

    const batchStatusOrder = [
      'CSO_TIM_STA_OPN',
      'CSO_TIM_STA_CHK',
      'CSO_TIM_STA_FRE',
    ]
    const orderedStatuses = [...statuses].sort(
      (a, b) =>
        batchStatusOrder.indexOf(a.value) - batchStatusOrder.indexOf(b.value),
    )

    const tableColumns = [
      {
        accessor: 'checkbox',
        title: '',
        variant: Column.ROWSELECT,
      },
      {
        accessor: 'date',
        title: t('timeTracking.columns.date'),
        variant: Column.WIDTH_120PX,
      },
      {
        accessor: 'teamMember',
        title: t('timeTracking.columns.teamMember'),
        variant: Column.WIDTH_160PX,
      },
      {
        accessor: 'project',
        title: t('timeTracking.columns.project'),
        variant: Column.WIDTH_260PX,
      },
      {
        accessor: 'trackedTime',
        title: t('timeTracking.columns.trackedTime'),
        variant: Column.WIDTH_120PX,
      },
      {
        accessor: 'billable',
        title: t('timeTracking.columns.billable'),
        variant: Column.WIDTH_120PX,
      },
      {
        accessor: 'nonBillable',
        title: t('timeTracking.columns.nonBillable'),
        variant: Column.WIDTH_140PX,
      },
      {
        accessor: 'status',
        title: t('timeTracking.columns.status'),
        variant: Column.WIDTH_120PX,
      },
    ]

    return (
      <div>
        <TimeTrackingControlBar
          weekRange={effectiveWeekRange}
          statusOptions={statuses}
          teamOptions={teamLeaders}
          teamMemberOptions={teamMemberOptions}
          teamLeaderIds={teamLeaderIds}
          initialFilters={initialFilters}
          onWeekChange={onWeekRangeChange}
          onNavigateWeek={handleNavigateWeek}
          onCurrentWeek={goToCurrentWeek}
          onFilterChange={handleFilterChange}
          onTeamLeaderSelect={setSelectedLeaderId}
          onLogTime={handleLogTime}
          onExportCSV={exportCSV}
          isExporting={isExporting}
          currentTab={TimeTrackingTab.ALL_TIMES}
          totals={totals}
        />

        <div className="row">
          <div className="col-sm-12">
            <DisplayCountIndicator />
            {!dataItems.length ? (
              <div>{t('timeTracking.noData')}</div>
            ) : (
              <>
                <div>
                  <div className="time-tracking-table-container">
                    <Table
                      columns={tableColumns}
                      className="time-tracking-compact-table time-tracking-all-times-table"
                      selectAllRows={handleSelectAll}
                      isAllRowsSelected={() => isAllSelected}
                    >
                      {dataItems.map((item) => {
                        const itemId = item.id()
                        return (
                          <TimerecordRow
                            key={itemId}
                            dataItem={item}
                            statusOptions={statusOptions}
                            isSelected={selectedRecordIds.has(itemId)}
                            canEdit={isEditable(item)}
                            onToggleSelection={() =>
                              handleToggleRecordSelection(itemId)
                            }
                            onEdit={() => openLogTimeDrawer(itemId)}
                            onCopy={() => handleDuplicateTimeRecord(itemId)}
                            onDelete={() => handleDeleteTimeRecord(itemId)}
                            onInfo={() => handleViewTimeRecord(itemId)}
                            onAdd={() => handleAddTimeRecord(item)}
                          />
                        )
                      })}
                    </Table>
                  </div>
                  {selectedRecordIds.size > 0 && (
                    <div className="batch-status-actions-overlay">
                      <div className="batch-status-actions">
                        {orderedStatuses.map((status) => (
                          <button
                            key={status.value}
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              handleBatchStatusUpdate(status.value)
                            }
                            type="button"
                          >
                            {t('timeTracking.markAs', { status: status.label })}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {hasMore && (
                  <div className="mt-4 text-center">
                    <button
                      className="btn btn-outline-primary"
                      onClick={loadMore}
                      type="button"
                    >
                      {t('common.loadMore')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <ViewLogTimeDrawer
          isOpen={viewDrawerOpen}
          onClose={() => setViewDrawerOpen(false)}
          data={selectedRecordData}
          onEdit={
            isSelectedRecordEditable
              ? () => {
                  if (selectedRecordId) {
                    setViewDrawerOpen(false)
                    openLogTimeDrawer(selectedRecordId)
                  }
                }
              : undefined
          }
          onDelete={
            isSelectedRecordEditable
              ? () => {
                  if (selectedRecordId) {
                    setViewDrawerOpen(false)
                    handleDeleteTimeRecord(selectedRecordId)
                  }
                }
              : undefined
          }
        />

        <ConfirmActionModal
          isOpen={pendingDeleteId !== null}
          title={t('timeTracking.deleteEntry.title')}
          message={t('timeTracking.deleteEntry.message')}
          confirmText={t('timeTracking.deleteEntry.confirm')}
          cancelText={t('common.cancel')}
          onConfirm={confirmDelete}
          onClose={() => setPendingDeleteId(null)}
        />
      </div>
    )
  },
)

export default AllTimesTab
